import React, { useState, useEffect } from 'react';
import produce from 'immer';

import Button, { BUTTON_TYPES } from '../../components/Button';
import ColorPicker from '../../components/ColorPicker';
import ControlBoard from '../../components/ControlBoard';
import Label from '../../components/Label';
import RangeSlider from '../../components/inputs/RangSlider';
import ThirdLevelBoard from '../../components/ThirdLevelBoard';
import Dropdown from '../../components/Dropdown';
import Checkbox from '../../components/inputs/Checkbox';

// import { DEFAULT_BG_COLOR } from '../../constants/defaultColors';

// ---------------------------------------------------------------------------------------------------------------------------
const BORDERS = {
  NONE: {
    label: 'none'
  },
  TOP: {
    label: 'Top Border',
    styleKey: 'borderTop'
  },
  RIGHT: {
    label: 'Right Border',
    styleKey: 'borderRight'
  },
  LEFT: {
    label: 'Left Border',
    styleKey: 'borderLeft'
  },
  BOTTOM: {
    label: 'Bottom Border',
    styleKey: 'borderBottom'
  },
};

const BORDER_STYLES = {
  NONE: 'none',
  HIDDEN: 'hidden',
  DASHED: 'dashed',
  DOTTED: 'dotted',
  DOUBLE: 'double',
  GROOVE: 'groove',
  INSET: 'inset',
  INHERIT: 'inherit',
  INITIAL: 'initial',
  SOLID: 'solid',
  RIDGE: 'ridge',
  OUTSET: 'outset',
};

// ---------------------------------------------------------------------------------------------------------------------------


export default function BorderController(props) {
  const { containerStyleData, setContainerStyleData, toggleBorderController } = props;
  const [restoreStyleDataPoint, setRestoreStyleDataPoint] = useState({}); // for reverting purpose
  const [activeBorder, toggleActiveBorder] = useState(BORDERS.NONE);
  const [colorPickerVisible, toggleColorPicker] = useState(false);
  // -------------------------------------------------------------------------------------

  const handleBorderRadius = (e) => {
    const amount = Number(e.target.value);
    const newContainerStyledata = produce(containerStyleData, (draft) => {
      draft.style.borderRadius = amount;
    });
    setContainerStyleData(newContainerStyledata);
  };

  const onActiveBorderChange = (thisBorder) => () => {
    toggleActiveBorder(thisBorder);
    setRestoreStyleDataPoint(containerStyleData);
  }

  const onBorderWidthChange = (e) => {
    const newWidth = Number(e.target.value);
    const newStyleData = produce(containerStyleData, (draft) => {
      draft.style[`${activeBorder.styleKey}${'Width'}`] = newWidth;
    });
    setContainerStyleData(newStyleData);
  };

  const onBorderStyleChange = (newStyle) => {
    const newStyleData = produce(containerStyleData, (draft) => {
      draft.style[`${activeBorder.styleKey}${'Style'}`] = newStyle;
    });
    setContainerStyleData(newStyleData);
  };

  const onBorderColorChange = (color) => {
    const selectedColor = color.hex;
    const newStyleData = produce(containerStyleData, (draft) => {
      draft.style[`${activeBorder.styleKey}${'Color'}`] = selectedColor;
    });
    setContainerStyleData(newStyleData);
  };

  const copyStyle = (target, checked) => {
    let borderWidthToCopy;
    let borderStyleToCopy;
    let borderColorToCopy;

    if (checked) {
      borderWidthToCopy = containerStyleData.style[`${activeBorder.styleKey}Width`];
      borderStyleToCopy = containerStyleData.style[`${activeBorder.styleKey}Style`];
      borderColorToCopy = containerStyleData.style[`${activeBorder.styleKey}Color`];
    } else {
      borderWidthToCopy = restoreStyleDataPoint.style[`${activeBorder.styleKey}Width`];
      borderStyleToCopy = containerStyleData.style[`${activeBorder.styleKey}Style`];
      borderColorToCopy = containerStyleData.style[`${activeBorder.styleKey}Color`];
    }

    setContainerStyleData(produce(containerStyleData, (draft) => {
      draft.style[`${target}Width`] = borderWidthToCopy;
      draft.style[`${target}Style`] = borderStyleToCopy;
      draft.style[`${target}Color`]= borderColorToCopy;
    }));
  }

  const onCheckboxChange = (e) => {
    switch (e.target.value) {
      case BORDERS.TOP.label: {
        copyStyle('borderTop', e.target.checked)
        return;
      }
      case BORDERS.RIGHT.label: {
        copyStyle('borderRight', e.target.checked)
        return;
      }
      case BORDERS.BOTTOM.label: {
        copyStyle('borderBottom', e.target.checked)
        return;
      }
      case BORDERS.LEFT.label: {
        copyStyle('borderLeft', e.target.checked)
        return;
      }
      default: {
        return;
      }
    }
  };

  const onIndiBorderModDone = () => {
    toggleActiveBorder(BORDERS.NONE);
  };

  const selectedBorderColor = containerStyleData.style[`${activeBorder.styleKey}${'Color'}`];
  const selectedBorderWidth = containerStyleData.style[`${activeBorder.styleKey}${'Width'}`];

  return (
    <ControlBoard headingLabel="Adjust Border" doneText="Finish" showCancel={false} onDone={() => toggleBorderController(false)}>
      <div style={{ position: 'relative' }}>
        {activeBorder.label === BORDERS.NONE.label && (
          <div>
            <RangeSlider
              label={`Radius (${containerStyleData.style.borderRadius}px)`}
              onChange={handleBorderRadius}
              value={containerStyleData.style.borderRadius}
              min={0}
            />
            <Button onClick={onActiveBorderChange(BORDERS.TOP)} label={BORDERS.TOP.label} block />
            <Button onClick={onActiveBorderChange(BORDERS.RIGHT)} label={BORDERS.RIGHT.label} block />
            <Button onClick={onActiveBorderChange(BORDERS.BOTTOM)} label={BORDERS.BOTTOM.label} block />
            <Button onClick={onActiveBorderChange(BORDERS.LEFT)} label={BORDERS.LEFT.label} block />
          </div>
        )}
        {activeBorder.label !== BORDERS.NONE.label && (
          <div style={{ position: 'absolute', zIndex: 999 }}>
            <ThirdLevelBoard onDone={onIndiBorderModDone} headingLabel={activeBorder.label}>
              {!colorPickerVisible &&
                <div>
                  <div>
                    <Label style={{ display: 'inline' }}>Color</Label>
                    <Button
                      type={BUTTON_TYPES.PILLS}
                      label={selectedBorderColor}
                      style={{ backgroundColor: selectedBorderColor, marginLeft: 5, marginTop: 5 }}
                      onClick={() => toggleColorPicker(true)}
                    />
                  </div>
                  <RangeSlider
                    label={`Width (${selectedBorderWidth}px)`}
                    onChange={onBorderWidthChange}
                    value={selectedBorderWidth}
                    min={0}
                  />
                  <Dropdown
                    label="Select Border Style"
                    options={Object.entries(BORDER_STYLES)}
                    onMenuClick={onBorderStyleChange}
                    id="border-style-selection"
                    containerStyle={{ marginBottom: 30 }}
                  />
                  <Label>Also Apply to</Label>
                  {activeBorder.label !== BORDERS.TOP.label &&
                    <Checkbox id="checkbox-apply-border-to-top" label="Top Border" onChange={onCheckboxChange} value={BORDERS.TOP.label} />
                  }
                  {activeBorder.label !== BORDERS.RIGHT.label &&
                    <Checkbox id="checkbox-apply-border-to-top" label="Right Border" onChange={onCheckboxChange} value={BORDERS.RIGHT.label} />
                  }
                  {activeBorder.label !== BORDERS.BOTTOM.label &&
                    <Checkbox id="checkbox-apply-border-to-top" label="Bottom Border" onChange={onCheckboxChange} value={BORDERS.BOTTOM.label} />
                  }
                  {activeBorder.label !== BORDERS.LEFT.label &&
                    <Checkbox id="checkbox-apply-border-to-top" label="Left Border" onChange={onCheckboxChange} value={BORDERS.LEFT.label} />
                  }
                </div>
              }
              {colorPickerVisible &&
                <ColorPicker
                  color={selectedBorderColor}
                  onChange={onBorderColorChange}
                  onDone={() => toggleColorPicker(false)}
                />
              }
            </ThirdLevelBoard>
          </div>
        )}
      </div>
    </ControlBoard>
  )
}
